import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../LoadingSpinner";

const MFASettings = ({ onMessage, onLoading }) => {
  const { user } = useAuth();
  const [mfaStatus, setMfaStatus] = useState({
    enabled: false,
    setupComplete: false,
    backupCodes: [],
    lastUsed: null,
  });
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [setupStep, setSetupStep] = useState(1);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);

  // Load MFA status on component mount
  useEffect(() => {
    loadMFAStatus();
  }, []);

  const loadMFAStatus = async () => {
    try {
      // TODO: Implement actual API call
      // const response = await authAPI.getMFAStatus();

      // Mock data
      setMfaStatus({
        enabled: false,
        setupComplete: false,
        backupCodes: [],
        lastUsed: null,
      });
    } catch (error) {
      onMessage({
        type: "error",
        text: "Failed to load MFA status",
      });
    }
  };

  const startMFASetup = async () => {
    onLoading(true);
    try {
      // TODO: Implement actual API call
      // const response = await authAPI.startMFASetup();

      // Mock QR code URL
      const mockQRCode = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==`;
      setQrCodeUrl(mockQRCode);
      setShowSetupModal(true);
      setSetupStep(1);
    } catch (error) {
      onMessage({
        type: "error",
        text: "Failed to start MFA setup",
      });
    } finally {
      onLoading(false);
    }
  };

  const verifyMFASetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      onMessage({
        type: "error",
        text: "Please enter a valid 6-digit code",
      });
      return;
    }

    onLoading(true);
    try {
      // TODO: Implement actual API call
      // const response = await authAPI.verifyMFASetup({ code: verificationCode });

      // Mock backup codes
      const mockBackupCodes = [
        "ABCD-EFGH-IJKL",
        "MNOP-QRST-UVWX",
        "YZAB-CDEF-GHIJ",
        "KLMN-OPQR-STUV",
        "WXYZ-ABCD-EFGH",
        "IJKL-MNOP-QRST",
        "UVWX-YZAB-CDEF",
        "GHIJ-KLMN-OPQR",
      ];

      setBackupCodes(mockBackupCodes);
      setSetupStep(3);

      // Update MFA status
      setMfaStatus({
        enabled: true,
        setupComplete: true,
        backupCodes: mockBackupCodes,
        lastUsed: null,
      });

      onMessage({
        type: "success",
        text: "MFA enabled successfully",
        details:
          "Your account is now protected with two-factor authentication.",
      });
    } catch (error) {
      onMessage({
        type: "error",
        text: "Invalid verification code",
        details:
          "Please check the code from your authenticator app and try again.",
      });
    } finally {
      onLoading(false);
    }
  };

  const disableMFA = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disable MFA? This will make your account less secure.",
      )
    ) {
      return;
    }

    onLoading(true);
    try {
      // TODO: Implement actual API call
      // await authAPI.disableMFA();

      setMfaStatus({
        enabled: false,
        setupComplete: false,
        backupCodes: [],
        lastUsed: null,
      });

      onMessage({
        type: "warning",
        text: "MFA disabled successfully",
        details: "Your account is now less secure. Consider re-enabling MFA.",
      });
    } catch (error) {
      onMessage({
        type: "error",
        text: "Failed to disable MFA",
      });
    } finally {
      onLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (
      !window.confirm(
        "This will invalidate your existing backup codes. Continue?",
      )
    ) {
      return;
    }

    onLoading(true);
    try {
      // TODO: Implement actual API call
      // const response = await authAPI.regenerateBackupCodes();

      const newBackupCodes = [
        "ABCD-EFGH-IJKL",
        "MNOP-QRST-UVWX",
        "YZAB-CDEF-GHIJ",
        "KLMN-OPQR-STUV",
        "WXYZ-ABCD-EFGH",
        "IJKL-MNOP-QRST",
        "UVWX-YZAB-CDEF",
        "GHIJ-KLMN-OPQR",
      ];

      setBackupCodes(newBackupCodes);
      setMfaStatus((prev) => ({
        ...prev,
        backupCodes: newBackupCodes,
      }));

      onMessage({
        type: "success",
        text: "Backup codes regenerated",
        details: "Save these new codes in a secure location.",
      });
    } catch (error) {
      onMessage({
        type: "error",
        text: "Failed to regenerate backup codes",
      });
    } finally {
      onLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const codes = mfaStatus.backupCodes.join("\n");
    const blob = new Blob(
      [
        `DZ Metrics Backup Codes\n\nGenerated: ${new Date().toLocaleDateString()}\n\n${codes}\n\nKeep these codes in a secure location.`,
      ],
      {
        type: "text/plain",
      },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dz-metrics-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyBackupCodes = () => {
    const codes = mfaStatus.backupCodes.join("\n");
    navigator.clipboard.writeText(codes).then(() => {
      onMessage({
        type: "success",
        text: "Backup codes copied to clipboard",
      });
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">
          Multi-Factor Authentication
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Add an extra layer of security to your account with 2FA
        </p>
      </div>

      {/* MFA Status */}
      <div
        className={`rounded-lg p-4 border ${
          mfaStatus.enabled
            ? "bg-green-50 border-green-200"
            : "bg-yellow-50 border-yellow-200"
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <span
              className={`text-2xl mr-3 ${
                mfaStatus.enabled ? "text-green-500" : "text-yellow-500"
              }`}
            >
              {mfaStatus.enabled ? "🛡️" : "⚠️"}
            </span>
            <div>
              <h4
                className={`text-sm font-medium ${
                  mfaStatus.enabled ? "text-green-900" : "text-yellow-900"
                }`}
              >
                Multi-Factor Authentication is{" "}
                {mfaStatus.enabled ? "Enabled" : "Disabled"}
              </h4>
              <p
                className={`text-sm mt-1 ${
                  mfaStatus.enabled ? "text-green-700" : "text-yellow-700"
                }`}
              >
                {mfaStatus.enabled
                  ? "Your account is protected with two-factor authentication"
                  : "Enable MFA to secure your account with an extra layer of protection"}
              </p>
              {mfaStatus.enabled && mfaStatus.lastUsed && (
                <p className="text-xs text-green-600 mt-1">
                  Last used: {new Date(mfaStatus.lastUsed).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            {!mfaStatus.enabled ? (
              <button
                onClick={startMFASetup}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm"
              >
                Enable MFA
              </button>
            ) : (
              <button
                onClick={disableMFA}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                Disable MFA
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Backup Codes Section */}
      {mfaStatus.enabled && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                Backup Codes
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                Use these codes to sign in if you lose access to your
                authenticator app
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowBackupCodes(!showBackupCodes)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                {showBackupCodes ? "Hide" : "Show"} Codes
              </button>
              <button
                onClick={regenerateBackupCodes}
                className="text-yellow-600 hover:text-yellow-700 text-sm font-medium"
              >
                Regenerate
              </button>
            </div>
          </div>

          {showBackupCodes && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                  {mfaStatus.backupCodes.map((code, index) => (
                    <div
                      key={index}
                      className="p-2 bg-white rounded border text-center"
                    >
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={downloadBackupCodes}
                  className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  Download
                </button>
                <button
                  onClick={copyBackupCodes}
                  className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  Copy
                </button>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Store these codes in a secure
                  location. Each code can only be used once and will be
                  invalidated after use.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MFA Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Enable Multi-Factor Authentication
              </h3>
              <button
                onClick={() => {
                  setShowSetupModal(false);
                  setSetupStep(1);
                  setVerificationCode("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {setupStep === 1 && (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    Scan this QR code with your authenticator app (Google
                    Authenticator, Authy, etc.)
                  </p>
                  <div className="bg-white p-4 border rounded-lg inline-block">
                    <img
                      src={qrCodeUrl}
                      alt="QR Code"
                      className="w-48 h-48 mx-auto bg-gray-100"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Can't scan? Use this secret key: ABCDEFGHIJKLMNOP
                  </p>
                </div>
                <button
                  onClick={() => setSetupStep(2)}
                  className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  I've Added the Account
                </button>
              </div>
            )}

            {setupStep === 2 && (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Enter the 6-digit verification code from your authenticator
                    app
                  </p>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) =>
                      setVerificationCode(
                        e.target.value.replace(/\D/g, "").substring(0, 6),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center text-lg font-mono"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setSetupStep(1)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={verifyMFASetup}
                    disabled={verificationCode.length !== 6}
                    className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    Verify & Enable
                  </button>
                </div>
              </div>
            )}

            {setupStep === 3 && (
              <div className="space-y-4">
                <div className="text-center">
                  <span className="text-4xl text-green-500">✅</span>
                  <h4 className="text-lg font-medium text-gray-900 mt-2">
                    MFA Enabled Successfully!
                  </h4>
                  <p className="text-sm text-gray-600">
                    Your account is now protected with two-factor
                    authentication.
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                  <h5 className="font-medium text-yellow-900 mb-2">
                    Save Your Backup Codes
                  </h5>
                  <p className="text-sm text-yellow-700 mb-3">
                    These codes can be used to access your account if you lose
                    your authenticator device.
                  </p>
                  <div className="bg-white rounded p-3 mb-3">
                    <div className="grid grid-cols-2 gap-1 text-xs font-mono">
                      {backupCodes.map((code, index) => (
                        <div key={index} className="text-center py-1">
                          {code}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={downloadBackupCodes}
                      className="flex-1 bg-yellow-600 text-white px-3 py-2 rounded text-sm hover:bg-yellow-700 transition-colors"
                    >
                      Download
                    </button>
                    <button
                      onClick={copyBackupCodes}
                      className="flex-1 bg-yellow-600 text-white px-3 py-2 rounded text-sm hover:bg-yellow-700 transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setShowSetupModal(false);
                    setSetupStep(1);
                    setVerificationCode("");
                  }}
                  className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Complete Setup
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MFASettings;
